/*
 * Copyright © 2021-present Tacnode Inc. All rights reserved.
 * The information contained in this software is confidential and proprietary to Tacnode Inc. and its subsidiaries. No part of this software may be used, copied, reproduced, modified, displayed, performed, executed, published, distributed, conveyed, stored, transmitted, or otherwise exploited in any form or by any means, including but not limited to electronic, mechanical, photocopying, recording, transcribing, without the prior written consent of Tacnode Inc.
 * Any violation of the above terms will constitute an infringement of copyright and will be subjected to legal action.
 */
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { PlatformContextlake } from 'account-client'
import styles from './index.less'
import { PlatformList } from '@/components/PlatformList'
import { RefreshListButton } from '@/components/PlatformList/RefreshListButton'
import { TPlatformListService } from '@/components/PlatformList/interface'
import { ALL_PAGES, POLL_TIME } from '@/constants'
import { LayoutOthers } from '@/layouts/LayoutOthers'
import { CreateContextlakeModal } from '@/pages/contextlake/Contextlakes/CreateContextlakeModal'
import { ContextlakeCard } from '@/pages/contextlake/Contextlakes/ContextlakeCard'
import { contextlakeIcon, isTacnode, officialSite } from '@/process.env'
import { accountClient } from '@/services/cloudClient/accountClient'

export function Contextlakes() {
  const { t } = useTranslation()
  const [showAlert, setShowAlert] = useState(false)
  const emitter = PlatformList.useCreateEmitter()

  const getContextlakeListService: TPlatformListService = (_, options) => {
    // return Promise.resolve({data: {items: []}})
    return accountClient.contextlakesApi.listContextlakes(ALL_PAGES, options).then(res => {
      setShowAlert(res.data.items.length > 0)
      // Backend returns items in unstable order; sort to keep card order consistent across refreshes
      res.data.items.sort(
        (a: PlatformContextlake, b: PlatformContextlake) =>
          (b.gmtCreated ?? 0) - (a.gmtCreated ?? 0) || (a.id ?? '').localeCompare(b.id ?? ''),
      )
      return res
    })
  }

  return (
    <LayoutOthers.MainContent title={t('Context lakes')}>
      {showAlert && <LayoutOthers.Alert className="margin-bottom-20">{t('data_cloud_desc')}</LayoutOthers.Alert>}
      <PlatformList
        emitter={emitter}
        service={getContextlakeListService}
        pollTime={POLL_TIME.normal}
        createTitle={() => (
          <>
            <RefreshListButton />
            <CreateContextlakeModal />
          </>
        )}
        emptyProps={{
          image: contextlakeIcon,
          description: (
            <>
              <div>{t('data_cloud_desc')}</div>
              {isTacnode && (
                <Trans i18nKey="refer_documentation_desc">
                  Please refer to the{' '}
                  <a href={`${officialSite}/docs/guides/platform/contextlake`} target="_blank">
                    documentation
                  </a>{' '}
                  for more information
                </Trans>
              )}
            </>
          ),
        }}
        createTiles={(contextlakeList: PlatformContextlake[]) => (
          <div className={styles.list}>
            {contextlakeList.map(item => (
              <ContextlakeCard key={item.id} contextlake={item} />
            ))}
          </div>
        )}
      />
    </LayoutOthers.MainContent>
  )
}
